import React from 'react';
import { Gauge, ShieldCheck, Zap, HardHat, Terminal } from 'lucide-react';

export interface FooterProps {
  onNavigate?: (path: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (onNavigate && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  return (
    <footer className="border-t border-border/80 bg-slate-100/70 dark:bg-slate-950 py-12 text-sm text-muted-foreground mt-auto">
      <div className="container grid gap-8 md:grid-cols-2 lg:grid-cols-5">
        {/* Brand Col */}
        <div className="space-y-3">
          <a
            href="/"
            onClick={(e) => handleLinkClick(e, '/')}
            className="inline-flex items-center gap-2"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded bg-amber-500 text-slate-950 font-black">
              <Gauge className="h-4 w-4 stroke-[2.5]" aria-hidden="true" />
            </div>
            <span className="font-bold text-foreground dark:text-white tracking-tight">MaintenancePrep</span>
          </a>
          <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
            Independent practice exams and diagnostic tools engineered for maintenance technicians, industrial electricians, millwrights, mechatronics specialists, and USPS Exam 955 candidates preparing for Ramsay and Postal Maintenance Assessments.
          </p>
          <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            System Status: All Systems Operational
          </div>
        </div>

        {/* Tracks Col */}
        <nav aria-label="Assessment Tracks">
          <h2 className="font-semibold text-foreground dark:text-white mb-3 text-xs uppercase tracking-wider">Assessment Tracks</h2>
          <ul className="space-y-2 text-xs">
            <li>
              <a
                href="/track/mechanical"
                onClick={(e) => handleLinkClick(e, '/track/mechanical')}
                className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              >
                Mechanical Maintenance (MEC-TEST)
              </a>
            </li>
            <li>
              <a
                href="/track/electrical"
                onClick={(e) => handleLinkClick(e, '/track/electrical')}
                className="hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                Electrical &amp; Electronics (ELE-TEST)
              </a>
            </li>
            <li>
              <a
                href="/track/multicraft"
                onClick={(e) => handleLinkClick(e, '/track/multicraft')}
                className="hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                Multicraft &amp; USPS Exam 955
              </a>
            </li>
            <li>
              <a
                href="/track/aptitude"
                onClick={(e) => handleLinkClick(e, '/track/aptitude')}
                className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              >
                Mechanical Aptitude &amp; Spatial (MAT)
              </a>
            </li>
            <li>
              <a
                href="/course/schematics"
                onClick={(e) => handleLinkClick(e, '/course/schematics')}
                className="hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
              >
                Electrical Schematics Course
              </a>
            </li>
            <li>
              <a
                href="/study-lab"
                onClick={(e) => handleLinkClick(e, '/study-lab')}
                className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              >
                Interactive Study Lab &amp; Formulas
              </a>
            </li>
            <li>
              <a
                href="/pricing"
                onClick={(e) => handleLinkClick(e, '/pricing')}
                className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              >
                Pricing &amp; 14-Day Free Trial
              </a>
            </li>
          </ul>
        </nav>

        {/* Features */}
        <div>
          <h2 className="font-semibold text-foreground dark:text-white mb-3 text-xs uppercase tracking-wider">Test Simulator</h2>
          <ul className="space-y-2 text-xs">
            <li className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" /> Untimed Study Mode with Instant Logic</li>
            <li className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden="true" /> Realistic 60-Minute Timed Mock Simulator</li>
            <li className="flex items-center gap-1.5"><Terminal className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" aria-hidden="true" /> Interactive Schematics &amp; Diagrams</li>
            <li className="flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" aria-hidden="true" /> Topic-by-Topic Mastery Analytics</li>
          </ul>
        </div>

        {/* Mobile Apps Col */}
        <div>
          <h2 className="font-semibold text-foreground dark:text-white mb-3 text-xs uppercase tracking-wider">Mobile Apps</h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">
            Native apps for iPhone, iPad, and Android launching soon for offline study.
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-border dark:border-slate-800 bg-card/80 dark:bg-slate-900/60 px-2.5 py-1.5 text-xs">
              <span className="font-medium text-foreground dark:text-white">iOS App</span>
              <span className="text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded">Coming Soon</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border dark:border-slate-800 bg-card/80 dark:bg-slate-900/60 px-2.5 py-1.5 text-xs">
              <span className="font-medium text-foreground dark:text-white">Android App</span>
              <span className="text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded">Coming Soon</span>
            </div>
          </div>
        </div>

        {/* Legal & Notice */}
        <div>
          <h2 className="font-semibold text-foreground dark:text-white mb-3 text-xs uppercase tracking-wider">Trademark Disclaimer</h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            MaintenancePrep is an independent educational prep platform and is not affiliated with, sponsored by, or endorsed by Ramsay Corporation, the United States Postal Service (USPS), or any test-administering employer. Ramsay, MEC-TEST, ELE-TEST, MultiCrafTest, and Ramsay MAT are trademarks or registered trademarks of Ramsay Corporation. USPS and Postal Exam 955 are trademarks or registered marks of the United States Postal Service. All test names and acronyms are used strictly for nominative, identification, and descriptive purposes.
          </p>
        </div>
      </div>

      <div className="container border-t border-border dark:border-slate-900 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          © {new Date().getFullYear()} MaintenancePrep. All rights reserved. Built by{' '}
          <a
            href="https://saram.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-700 dark:text-slate-400 hover:text-foreground dark:hover:text-white underline decoration-slate-400 dark:decoration-slate-600 underline-offset-2 transition-colors font-medium"
          >
            saram consulting
          </a>
          .
        </div>
        <div className="flex gap-4 mt-2 sm:mt-0">
          <a
            href="/pricing"
            onClick={(e) => handleLinkClick(e, '/pricing')}
            className="hover:text-foreground transition-colors"
          >
            Pricing
          </a>
          <a
            href="/course/schematics"
            onClick={(e) => handleLinkClick(e, '/course/schematics')}
            className="hover:text-foreground transition-colors"
          >
            Schematics Course
          </a>
          <a
            href="/study-lab"
            onClick={(e) => handleLinkClick(e, '/study-lab')}
            className="hover:text-foreground transition-colors"
          >
            Study Lab
          </a>
        </div>
      </div>
    </footer>
  );
}
